(ns common.datascript.schema)

;; schema construction functions

(defn attr-unique-identity
  "Mark an attribute as unique, but allow upsert behavior"
  [id-attr]
  {id-attr {:db/unique :db.unique/identity}})

(defn attr-unique-value
  "Mark an attribute as unique, do not allow upsert behavior"
  [id-attr]
  {id-attr {:db/unique :db.unique/value}})

(defn rename-attribute [id-attr attr-name]
  (keyword (namespace id-attr) attr-name))

(defn owner-id-attr [id-attr]
  (rename-attribute id-attr "owner-id"))

(defn unique-id-attr [id-attr]
  (rename-attribute id-attr "unique-id"))

(defn attr-unique-within-container [id-attr]
  (let [owner-id (owner-id-attr id-attr)]
    {owner-id {:db/valueType :db.type/ref}
     id-attr {:validation/unique-within-container? true}
     (unique-id-attr id-attr)
     {:db/tupleAttrs [owner-id id-attr]
      :db/unique :db.unique/value}}))

(defn attr-ref-one [id-attr]
  {id-attr {:db/cardinality :db.cardinality/one
            :db/valueType :db.type/ref
            :db/isComponent false}})

(defn attr-component-one [id-attr]
  (assoc-in (attr-ref-one id-attr)
            [id-attr :db/isComponent] true))

(defn attr-component-many [id-attr]
  (assoc-in (attr-component-one id-attr)
            [id-attr :db/cardinality] :db.cardinality/many))

(defn association-ref-attr [entity-name attr]
  (keyword
   entity-name
   (str "ref-" (namespace attr) "-" (subs (name attr) 1)
        "-dont-use-in-code-directly")))

(defn attr-unique-association [entity-name & reverse-attrs]
  (let [ref-attrs (map (partial association-ref-attr entity-name) reverse-attrs)]
    (apply merge
           (conj (map attr-ref-one ref-attrs)
                 {(keyword entity-name "unique-id")
                  {:db/tupleAttrs ref-attrs
                   :db/unique :db.unique/value}}))))

;; schema query functions

(defn ref? [schema attr]
  (= (get-in schema [attr :db/valueType]) :db.type/ref))

(defn calculated? [schema attr]
  (some? (get-in schema [attr :db/tupleAttrs])))

(defn unique-within-container? [schema attr]
  (get-in schema [attr :validation/unique-within-container?] false))

;; generic mutation functions

(defn set-association [entity-name ref-values entity-value-map]
  (reduce-kv
   (fn [res ref-attr ref-value]
     (assoc res (association-ref-attr entity-name ref-attr) ref-value))
   (merge entity-value-map ref-values)
   ref-values))

;; domain-specific schema definitions (per entity)
;; my wish for the future is that we derive all these datascript schemas
;; directly from the malli schemas of each entity, so we have a single source of
;; truth

;; content configuration page

(def content-configuration-schema
  (merge
   (attr-unique-identity :content-configuration/id)
   (attr-component-many :content-configuration/rule)
   (attr-component-many :content-configuration/placeholder)
   (attr-component-many :content-configuration/context-source)))

(def placeholder-expression-schema
  (merge
   (attr-unique-association :placeholder-expression
                            :placeholder/_override
                            :rule/_assignment)
   (attr-component-many :singular-placeholder-expression/macro)
   (attr-component-many :collection-placeholder-expression/item)
   (attr-component-many :collection-item/override)))

(def placeholder-schema
  (merge
   (attr-unique-within-container :placeholder/name)
   (attr-component-many :placeholder/override)
   (attr-component-one :placeholder/default-value)
   (attr-component-many :collection-placeholder/structure)))

(def rule-schema
  (merge
   (attr-unique-identity :rule/name)
   (attr-component-many :rule/condition)
   (attr-component-many :rule/assignment)))

(def context-source-schema
  (merge
   (attr-unique-identity :context-source/alias)
   (attr-component-many :context-source/macro)
   ;; feed-import
   (attr-unique-within-container :feed-context-source-lookup/id)
   (attr-component-many :feed-context-source/lookup)
   (attr-component-one :feed-context-source-lookup/macro)
   ;; custom
   (attr-unique-within-container :custom-schema-selector/name)
   (attr-component-many :custom-context-source/selector)
   ;; manual-split-testing
   (attr-unique-within-container :split-test-split/id)
   (attr-component-many :manual-split-testing-context-source/split)
   ;; dsp-signal
   (attr-unique-within-container :dsp-signal/name)
   (attr-component-many :dsp-signal-context-source/signal)))

;; content variant page

(def content-variant-schema
  (merge
   (attr-unique-identity :variant-feed/id)
   (attr-component-many :variant-feed/variant-feed-key)))

(def targeting-key-schema
  (merge
   (attr-unique-identity :variant-feed-key/name)
   (attr-ref-one :targeting-key/placeholder)
   (attr-ref-one :dynamic-targeting-key/rule)
   (attr-ref-one :mapped-targeting-value/rule)
   (attr-component-many :targeting-key/targeting-value)
   (attr-unique-within-container :targeting-value/value)
   (attr-ref-one :targeting-value/placeholder-expression)))

(def optimizing-key-schema
  (merge
   (attr-unique-identity :variant-feed-key/name)
   (attr-component-many :optimizing-key/optimizing-arm)
   (attr-unique-within-container :optimizing-arm/value)))

;; combination of all entity schemas

(def schema
  (merge
   content-configuration-schema
   placeholder-schema
   rule-schema
   context-source-schema
   placeholder-expression-schema
   content-variant-schema
   targeting-key-schema
   optimizing-key-schema))
